import * as React from 'react';
import Link from 'next/link';

import { YandexEventMap } from '@/features/event-page/contacts/yandex-event-map';
import { getActivityCoverImage } from '@/features/events-page/lib/activity-presentation';
import { EventType } from '@/features/events-page/list/events.const';
import { TOURNAMENT_TIME_ZONE } from '@/features/tournament-page/lib/format';

import {
  isLeagueSportLaunched,
  leagueDivisionPath,
  leagueTeamPath,
  parseDivisionId,
  parseLeagueSportId,
  type LeagueSportId,
} from '@/entities/league';

import type { PublicTournamentTeam } from '@/shared/api/event-public';
import type { PublicActivityVenue } from '@/shared/api/public-activity-venue';

import * as S from './event-detail.styled';

type EventDetailProps = {
  downloadAppLabel: string;
  event: EventType;
  venue?: PublicActivityVenue | null;
  openAppLabel: string;
  openAppUrl: string;
  storeUrl: string;
  /** Нажатие «Скачать приложение»: страница запоминает приглашение до установки. */
  onDownloadAppClick?: () => void;
  /**
   * Своя шапка вместо стандартной — например, веб-карточка турнира. Дата и место тогда уже в ней, а организатор
   * и тип участия остаются в сводке под шапкой.
   */
  hero?: React.ReactNode;
  /** Живое число команд (веб-карточка опрашивает его раз в 30 с), чтобы сводка турнира не расходилась со счётчиком. */
  liveRegisteredTeams?: number;
};

const TYPE_LABELS: Record<string, string> = {
  Free: 'Бесплатно',
  Paid: 'Платно',
};

const STATUS_LABELS: Record<string, string> = {
  Active: 'Активно',
  Open: 'Открыто',
  Inactive: 'Черновик',
  Pending: 'На модерации',
  Declined: 'Отклонено',
};

const KIND_LABELS: Record<EventType['eventKind'], string> = {
  Event: 'Событие',
  Challenge: 'Вызов',
  Tournament: 'Турнир',
};

const TOURNAMENT_STATUS_LABELS: Record<string, string> = {
  Draft: 'Черновик',
  RegistrationOpen: 'Регистрация открыта',
  RegistrationClosed: 'Регистрация закрыта',
  PaymentCollection: 'Сбор оплаты',
  DrawPending: 'Ожидает жеребьевку',
  DrawCompleted: 'Жеребьевка готова',
  GroupStage: 'Групповой этап',
  Playoff: 'Плей-офф',
  Semifinal: 'Полуфинал',
  Final: 'Финал',
  Finished: 'Завершен',
  Cancelled: 'Отменен',
};

const MATCH_STAGE_LABELS: Record<string, string> = {
  Group: 'Группа',
  Semifinal: 'Полуфинал',
  ThirdPlace: 'Матч за 3 место',
  Final: 'Финал',
};

const MATCH_STATUS_LABELS: Record<string, string> = {
  Scheduled: 'Запланирован',
  InProgress: 'Идет',
  Finished: 'Завершен',
  Cancelled: 'Отменен',
  TechnicalDefeat: 'Техническое поражение',
};

const TEAM_CATEGORY_LABELS: Record<string, string> = {
  Mixed: 'Микст',
  Women: 'Женские',
  Men: 'Мужские',
};

const TEAM_LEVEL_LABELS: Record<string, string> = {
  Pro: 'Профи',
  Amateur: 'Любители',
};

const METRIC_LABELS: Record<string, string> = {
  Steps: 'шагов',
  Distance: 'км',
  Calories: 'ккал',
  CaloriesBurned: 'ккал',
  RunningDistance: 'км беговых тренировок',
};

const METRIC_TITLE_LABELS: Record<string, string> = {
  Steps: 'Шаги',
  Distance: 'Километры',
  Calories: 'Калории',
  CaloriesBurned: 'Сожжённые калории',
  RunningDistance: 'Километры беговых тренировок',
};

function getLabel(labels: Record<string, string>, value?: string | null) {
  if (!value) return 'Не указано';
  return labels[value] ?? value;
}

function formatIsoDate(value?: string | null) {
  if (!value) return 'Не указано';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('ru-RU', {
    // Match the tournament card and keep server HTML identical across browser time zones.
    timeZone: TOURNAMENT_TIME_ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatMoneyMinor(value?: number | null, currency = 'RUB') {
  if (!value) return 'Бесплатно';

  return new Intl.NumberFormat('ru-RU', {
    currency,
    style: 'currency',
    maximumFractionDigits: 0,
  }).format(value / 100);
}

/** «Женские · Профи»: the tournament's League division, or the half the organizer set; null when neither is set. */
function getTeamDivisionLabel(event: EventType) {
  const parts = [
    event.teamCategory ? TEAM_CATEGORY_LABELS[event.teamCategory] : undefined,
    event.teamLevel ? TEAM_LEVEL_LABELS[event.teamLevel] : undefined,
  ].filter((part): part is string => Boolean(part));

  return parts.length > 0 ? parts.join('\u00A0·\u00A0') : null;
}

/** The League sport of a tournament that counts towards a launched League rating; null otherwise. */
function getRatedLeagueSport(event: EventType): LeagueSportId | null {
  if (event.eventKind !== 'Tournament' || !event.ratingEnabled) return null;

  const sport = parseLeagueSportId(event.sportType?.key);

  return sport && isLeagueSportLaunched(sport) ? sport : null;
}

/** The League division page of a rated tournament with both halves of its division set. */
function getLeagueDivisionHref(event: EventType, sport: LeagueSportId | null) {
  const divisionId = parseDivisionId(`${event.teamCategory ?? ''}-${event.teamLevel ?? ''}`.toLowerCase());

  return sport && divisionId ? leagueDivisionPath(sport, divisionId) : null;
}

/**
 * The League sport for team links: only a finished rated tournament with a full division puts its teams into the
 * tables, and a team page exists only for a team with a place there (the division page renders even empty).
 * A link can still lead to a 404 the team page caches only briefly: right after the finish, until the rating applies
 * the tournament, and for a team linked by hand that never played.
 */
function getLeagueTeamLinkSport(event: EventType): LeagueSportId | null {
  const sport = getRatedLeagueSport(event);
  const status = event.tournamentStatus ?? event.tournament?.status;

  return status === 'Finished' && getLeagueDivisionHref(event, sport) ? sport : null;
}

export function getEventCoverImage(event: EventType) {
  return getActivityCoverImage(event);
}

function getTournamentSummary(event: EventType) {
  const sportName = event.sportType?.name || 'спорту';
  const teams = event.tournament
    ? `${event.tournament.registeredTeamsCount}${
        event.tournament.settings?.maxTeams ? ` из ${event.tournament.settings.maxTeams}` : ''
      } команд`
    : 'команды собираются';
  const participants = event.maxParticipants ? ` Максимум участников — ${event.maxParticipants}.` : '';

  return `Турнир по направлению «${sportName}»: ${teams}.${participants}`;
}

function getPrimaryDescription(event: EventType) {
  if (event.eventKind === 'Tournament') {
    return getTournamentSummary(event);
  }

  return event.description || event.shortDescription || 'Подробности события появятся в Sportyfinder.';
}

const EventHero = (props: Omit<EventDetailProps, 'hero' | 'liveRegisteredTeams'>) => {
  const { event, openAppUrl, storeUrl, openAppLabel, downloadAppLabel, onDownloadAppClick } = props;
  const kindLabel = KIND_LABELS[event.eventKind];
  const divisionLabel = event.eventKind === 'Tournament' ? getTeamDivisionLabel(event) : null;
  const divisionHref = divisionLabel ? getLeagueDivisionHref(event, getRatedLeagueSport(event)) : null;
  const challengeMetric = event.victoryCondition?.metric || event.victoryMetric;
  const sportName =
    event.sportType?.name ||
    (event.eventKind === 'Challenge' ? getLabel(METRIC_TITLE_LABELS, challengeMetric) : 'Спорт');
  const coverImage = getEventCoverImage(event);

  return (
    <S.Hero>
      <S.HeroContent>
        <div>
          <S.EyebrowRow>
            <S.Pill>{kindLabel}</S.Pill>
            <S.Pill $tone="light">{sportName}</S.Pill>
            {divisionLabel && divisionHref ? <S.PillLink href={divisionHref}>{divisionLabel}</S.PillLink> : null}
            {divisionLabel && !divisionHref ? <S.Pill $tone="light">{divisionLabel}</S.Pill> : null}
            <S.Pill $tone="dark">{getLabel(STATUS_LABELS, event.status)}</S.Pill>
          </S.EyebrowRow>
          <S.Title>{event.title}</S.Title>
          <S.Summary>{getPrimaryDescription(event)}</S.Summary>
        </div>
        <S.HeroActions>
          <S.ActionLink href={openAppUrl}>{openAppLabel}</S.ActionLink>
          <S.ActionLink $variant="secondary" href={storeUrl} onClick={onDownloadAppClick}>
            {downloadAppLabel}
          </S.ActionLink>
        </S.HeroActions>
      </S.HeroContent>
      <S.HeroMedia>
        <S.HeroImage alt={`Обложка события «${event.title}»`} src={coverImage} />
      </S.HeroMedia>
    </S.Hero>
  );
};

const BaseStats = ({ event, withSchedule = true }: { event: EventType; withSchedule?: boolean }) => {
  return (
    <S.StatGrid>
      {withSchedule ? (
        <>
          <S.StatCard>
            <S.StatLabel>Дата и время</S.StatLabel>
            <S.StatValue>{event.dates[0] || 'Не указано'}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Место</S.StatLabel>
            <S.StatValue>{event.contacts.address || 'Адрес уточняется'}</S.StatValue>
          </S.StatCard>
        </>
      ) : null}
      <S.StatCard>
        <S.StatLabel>Организатор</S.StatLabel>
        <S.StatValue>{event.authorName || 'Sportyfinder'}</S.StatValue>
      </S.StatCard>
      <S.StatCard>
        <S.StatLabel>Участие</S.StatLabel>
        <S.StatValue>{getLabel(TYPE_LABELS, event.type)}</S.StatValue>
      </S.StatCard>
    </S.StatGrid>
  );
};

/** A team name, linked to the team's League page when the tournament put its teams into the League tables. */
function renderTeamName(
  team: PublicTournamentTeam | null | undefined,
  fallback: string,
  leagueSport: LeagueSportId | null,
) {
  const name = team?.name || fallback;

  if (!leagueSport || !team?.teamProfile) return name;

  return <S.TeamLink href={leagueTeamPath(leagueSport, team.teamProfile.slug)}>{name}</S.TeamLink>;
}

const TournamentTemplate = ({ event, liveRegisteredTeams }: { event: EventType; liveRegisteredTeams?: number }) => {
  const { tournament } = event;
  const settings = tournament?.settings;
  const leagueSport = getLeagueTeamLinkSport(event);

  if (!tournament) {
    return null;
  }

  return (
    <>
      <S.Section>
        <S.SectionHeader>
          <S.SectionTitle>Турнирная сводка</S.SectionTitle>
          <S.Pill>{getLabel(TOURNAMENT_STATUS_LABELS, tournament.status)}</S.Pill>
        </S.SectionHeader>
        <S.StatGrid>
          <S.StatCard>
            <S.StatLabel>Команд собрано</S.StatLabel>
            <S.StatValue>
              {liveRegisteredTeams ?? tournament.registeredTeamsCount}
              {settings?.maxTeams ? ` из ${settings.maxTeams}` : ''}
            </S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Игроков в командах</S.StatLabel>
            <S.StatValue>{tournament.confirmedParticipantsCount}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Размер команды</S.StatLabel>
            <S.StatValue>{settings?.teamSize ?? 'Не указан'}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Максимум участников</S.StatLabel>
            <S.StatValue>{event.maxParticipants ?? 'Не указан'}</S.StatValue>
          </S.StatCard>
        </S.StatGrid>
      </S.Section>

      <S.Section>
        <S.SectionHeader>
          <S.SectionTitle>Параметры турнира</S.SectionTitle>
          <S.Pill $tone="light">{settings?.format || 'Формат уточняется'}</S.Pill>
        </S.SectionHeader>
        <S.CardGrid>
          <S.SmallCard>
            <S.SmallTitle>Командный лимит</S.SmallTitle>
            <S.SmallText>
              {settings?.minTeams ?? '—'}
              {settings?.maxTeams ? `-${settings.maxTeams}` : ''} команд
            </S.SmallText>
          </S.SmallCard>
          <S.SmallCard>
            <S.SmallTitle>Состав команды</S.SmallTitle>
            <S.SmallText>
              {settings?.minTeamMembers ?? settings?.teamSize ?? '—'}
              {settings?.maxTeamMembers ? `-${settings.maxTeamMembers}` : ''} игроков
            </S.SmallText>
          </S.SmallCard>
          <S.SmallCard>
            <S.SmallTitle>Регистрация команд</S.SmallTitle>
            <S.SmallText>{settings?.teamRegistrationMode || 'Уточняется'}</S.SmallText>
          </S.SmallCard>
        </S.CardGrid>
      </S.Section>

      <S.Section>
        <S.SectionTitle>Регистрация и оплата</S.SectionTitle>
        <S.CardGrid>
          <S.SmallCard>
            <S.SmallTitle>Окончание регистрации</S.SmallTitle>
            <S.SmallText>{formatIsoDate(settings?.registrationEndDate)}</S.SmallText>
          </S.SmallCard>
          <S.SmallCard>
            <S.SmallTitle>Жеребьевка</S.SmallTitle>
            <S.SmallText>{formatIsoDate(settings?.drawDate)}</S.SmallText>
          </S.SmallCard>
          <S.SmallCard>
            <S.SmallTitle>Взнос команды</S.SmallTitle>
            <S.SmallText>{formatMoneyMinor(settings?.teamFeeAmountMinor, settings?.currency)}</S.SmallText>
          </S.SmallCard>
        </S.CardGrid>
      </S.Section>

      {tournament.teams.length > 0 && (
        <S.Section>
          <S.SectionTitle>Команды</S.SectionTitle>
          <S.CardGrid>
            {tournament.teams.map((team) => (
              <S.SmallCard key={team.id}>
                <S.SmallTitle>{renderTeamName(team, team.name, leagueSport)}</S.SmallTitle>
                <S.SmallText>
                  Посев #{team.seed} · {team.membersCount} игроков · {getLabel(STATUS_LABELS, team.status)}
                </S.SmallText>
              </S.SmallCard>
            ))}
          </S.CardGrid>
        </S.Section>
      )}

      {tournament.standings.length > 0 && (
        <S.Section>
          <S.SectionTitle>Турнирная таблица</S.SectionTitle>
          <S.TableWrap>
            <S.Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Команда</th>
                  <th>И</th>
                  <th>В</th>
                  <th>П</th>
                  <th>Очки</th>
                  <th>Разница</th>
                </tr>
              </thead>
              <tbody>
                {tournament.standings.map((standing) => (
                  <tr key={`${standing.teamId}-${standing.rank}`}>
                    <td>{standing.rank}</td>
                    <td>{renderTeamName(standing.team, standing.teamId, leagueSport)}</td>
                    <td>{standing.played}</td>
                    <td>{standing.wins}</td>
                    <td>{standing.losses}</td>
                    <td>{standing.points}</td>
                    <td>{standing.setDiff}</td>
                  </tr>
                ))}
              </tbody>
            </S.Table>
          </S.TableWrap>
        </S.Section>
      )}

      {tournament.matches.length > 0 && (
        <S.Section>
          <S.SectionTitle>Матчи</S.SectionTitle>
          <S.CardGrid>
            {tournament.matches.map((match) => (
              <S.SmallCard key={match.id}>
                <S.SmallTitle>
                  {renderTeamName(match.teamA, 'Команда A', leagueSport)} —{' '}
                  {renderTeamName(match.teamB, 'Команда B', leagueSport)}
                </S.SmallTitle>
                <S.SmallText>
                  {getLabel(MATCH_STAGE_LABELS, match.stage)} · Раунд {match.round} ·{' '}
                  {getLabel(MATCH_STATUS_LABELS, match.status)}
                </S.SmallText>
                {match.result && (
                  <S.SmallText>
                    Счет: {match.result.teamAScore}:{match.result.teamBScore}
                  </S.SmallText>
                )}
              </S.SmallCard>
            ))}
          </S.CardGrid>
        </S.Section>
      )}
    </>
  );
};

const ChallengeTemplate = ({ event }: { event: EventType }) => {
  const metric = event.victoryCondition?.metric || event.victoryMetric;

  return (
    <>
      <S.Section>
        <S.SectionHeader>
          <S.SectionTitle>Условие вызова</S.SectionTitle>
          <S.Pill $tone="light">{getLabel(METRIC_TITLE_LABELS, metric)}</S.Pill>
        </S.SectionHeader>
        <S.StatGrid>
          <S.StatCard>
            <S.StatLabel>Цель</S.StatLabel>
            <S.StatValue>
              {event.victoryCondition?.targetValue ?? event.victoryTargetValue ?? 'Не указана'}{' '}
              {getLabel(METRIC_LABELS, metric)}
            </S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Участников сейчас</S.StatLabel>
            <S.StatValue>{event.participantsTotal ?? 0}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Максимум участников</S.StatLabel>
            <S.StatValue>{event.maxParticipants ?? 'Не указан'}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Статус</S.StatLabel>
            <S.StatValue>{getLabel(STATUS_LABELS, event.status)}</S.StatValue>
          </S.StatCard>
        </S.StatGrid>
      </S.Section>

      <S.Section>
        <S.SectionTitle>Описание вызова</S.SectionTitle>
        <S.Text>{event.description || 'Описание вызова появится позже.'}</S.Text>
      </S.Section>

      {event.regulationsText && (
        <S.Section>
          <S.SectionTitle>Регламент</S.SectionTitle>
          <S.Text>{event.regulationsText}</S.Text>
        </S.Section>
      )}

      {event.sponsors && event.sponsors.length > 0 && (
        <S.Section>
          <S.SectionTitle>Спонсоры</S.SectionTitle>
          <S.CardGrid>
            {event.sponsors.map((sponsor) => (
              <S.SmallCard key={sponsor.id}>
                <S.SmallTitle>{sponsor.text}</S.SmallTitle>
                <a href={sponsor.url} rel="noopener noreferrer" target="_blank">
                  Сайт спонсора
                </a>
              </S.SmallCard>
            ))}
          </S.CardGrid>
        </S.Section>
      )}

      {event.leaderboardTop3 && event.leaderboardTop3.length > 0 && (
        <S.Section>
          <S.SectionTitle>Лидеры</S.SectionTitle>
          <S.CardGrid>
            {event.leaderboardTop3.map((leader, index) => (
              <S.SmallCard key={leader.userId || index}>
                <S.SmallTitle>
                  #{leader.rank ?? index + 1} {leader.displayName || leader.username || leader.firstName || 'Участник'}
                </S.SmallTitle>
                <S.SmallText>
                  {leader.value ?? 0} из {leader.targetValue ?? event.victoryTargetValue ?? 0}
                </S.SmallText>
              </S.SmallCard>
            ))}
          </S.CardGrid>
        </S.Section>
      )}
    </>
  );
};

const StandardEventTemplate = ({ event }: { event: EventType }) => {
  return (
    <>
      <S.Section>
        <S.SectionHeader>
          <S.SectionTitle>Информация</S.SectionTitle>
          <S.Pill>{event.sportType?.name || 'Спорт'}</S.Pill>
        </S.SectionHeader>
        <S.Text>{event.description || 'Описание события появится позже.'}</S.Text>
      </S.Section>

      <S.Section>
        <S.SectionTitle>Состав</S.SectionTitle>
        <S.StatGrid>
          <S.StatCard>
            <S.StatLabel>Записалось</S.StatLabel>
            <S.StatValue>{event.participantsTotal ?? 0}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Максимум участников</S.StatLabel>
            <S.StatValue>{event.maxParticipants ?? 'Не указан'}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Формат</S.StatLabel>
            <S.StatValue>{getLabel(TYPE_LABELS, event.type)}</S.StatValue>
          </S.StatCard>
          <S.StatCard>
            <S.StatLabel>Статус</S.StatLabel>
            <S.StatValue>{getLabel(STATUS_LABELS, event.status)}</S.StatValue>
          </S.StatCard>
        </S.StatGrid>
      </S.Section>
    </>
  );
};

const VenueSection = ({ event, venue }: Pick<EventDetailProps, 'event' | 'venue'>) => {
  return (
    <S.Section>
      <S.SectionTitle>Место проведения</S.SectionTitle>
      <S.VenueGrid>
        <S.VenueInfo>
          {venue && <S.SmallTitle>{venue.name}</S.SmallTitle>}
          <S.SmallTitle>{event.contacts.address || 'Адрес уточняется'}</S.SmallTitle>
          <S.SmallText>{event.dates[0] || 'Дата уточняется'}</S.SmallText>
          {venue && (
            <S.SmallText>
              <S.TeamLink href={`/venues/${venue.id}`}>Площадка и бронирование — {venue.name}</S.TeamLink>
            </S.SmallText>
          )}
        </S.VenueInfo>
        {event.coordinates ? (
          <S.VenueMapBox>
            <YandexEventMap
              address={event.contacts.address || 'Адрес уточняется'}
              coordinates={event.coordinates}
              title={event.title}
            />
          </S.VenueMapBox>
        ) : (
          <S.SmallCard>
            <S.SmallTitle>Карта недоступна</S.SmallTitle>
            <S.SmallText>Точка появится после уточнения координат.</S.SmallText>
          </S.SmallCard>
        )}
      </S.VenueGrid>
    </S.Section>
  );
};

const getSportLandingHref = (event: EventType) => {
  const address = event.contacts.address.toLocaleLowerCase();
  const isMoscow =
    address.includes('москв') ||
    (event.coordinates !== null &&
      event.coordinates !== undefined &&
      event.coordinates.latitude >= 55.45 &&
      event.coordinates.latitude <= 56.05 &&
      event.coordinates.longitude >= 36.8 &&
      event.coordinates.longitude <= 38.2);

  if (!isMoscow) return '/events';

  const sportName = event.sportType?.name.toLocaleLowerCase() ?? '';

  if (sportName.includes('йог') || sportName.includes('yoga')) return '/events/moscow/yoga';
  if (sportName.includes('волейбол') || sportName.includes('volleyball')) return '/events/moscow/volleyball';
  if (sportName.includes('бег') || sportName.includes('running')) return '/events/moscow/running';

  return '/events/moscow';
};

export const EventDetail = (props: EventDetailProps) => {
  const { event, hero, liveRegisteredTeams, venue } = props;

  return (
    <S.Page>
      {hero ? (
        <>
          {hero}
          <BaseStats event={event} withSchedule={false} />
        </>
      ) : (
        <>
          <EventHero {...props} />
          <BaseStats event={event} />
        </>
      )}
      {event.eventKind === 'Tournament' && (
        <TournamentTemplate event={event} liveRegisteredTeams={liveRegisteredTeams} />
      )}
      {event.eventKind === 'Challenge' && <ChallengeTemplate event={event} />}
      {event.eventKind === 'Event' && <StandardEventTemplate event={event} />}
      <VenueSection event={event} venue={venue} />
      <S.RelatedNav aria-label="Связанные разделы">
        <Link href="/events">Все события</Link>
        <Link href={getSportLandingHref(event)}>{event.sportType?.name || 'Спорт в Москве'}</Link>
        <Link href="/venues/moscow">Спортивные площадки</Link>
      </S.RelatedNav>
    </S.Page>
  );
};
